import { useEffect, useState } from 'react';
import { useBlocker } from '@/hooks/useBlocker';
import Modal from '@/components/base/Modal';
import { useSalesSettings } from '@/hooks/useSalesSettings';
import {
  MonedaSection,
  ImpuestosSection,
  DescuentosSection,
  MetodosPagoSection,
  EfectivoSection,
} from './components/SectionsBasic';
import {
  NumeracionSection,
  EstadoVentaSection,
  RedondeoSection,
  InventarioSection,
  ConfirmacionSection,
} from './components/SectionsNumbering';
import {
  ImpresionSection,
  InfoFacturaSection,
  MensajeFinalSection,
} from './components/SectionsInvoice';
import SettingsBreadcrumb from '@/pages/settings/components/SettingsBreadcrumb';

export default function SalesSettingsPage() {
  const {
    settings,
    loading,
    saving,
    error,
    success,
    dirty,
    patch,
    save,
    restoreDefaults,
    reload,
    clearMessages,
  } = useSalesSettings();

  const [restoreOpen, setRestoreOpen] = useState(false);
  const blocker = useBlocker(dirty);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const handleSave = async () => {
    await save();
  };

  const handleRestoreConfirm = async () => {
    setRestoreOpen(false);
    await restoreDefaults();
  };

  const handleBlockSave = async () => {
    const ok = await save();
    if (ok) blocker.proceed();
  };

  const handleBlockDiscard = () => {
    blocker.proceed();
  };

  return (
    <>
      <div className="max-w-6xl mx-auto">
        {/* Encabezado */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <SettingsBreadcrumb label="Configuración de Venta" icon="ri-shopping-cart-2-line" />
            <h1 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              Configuración de Venta
            </h1>
            <p className="mt-2 text-foreground-600 max-w-2xl">
              Configura los parámetros que utilizará el sistema al realizar ventas.
            </p>
          </div>
          {dirty && (
            <span className="inline-flex items-center gap-1.5 self-start sm:self-auto rounded-full bg-accent-100 text-accent-900 px-3 py-1.5 text-xs font-medium whitespace-nowrap">
              <span className="w-2 h-2 rounded-full bg-accent-500" />
              Hay cambios sin guardar
            </span>
          )}
        </div>

        {/* Mensajes */}
        {success && (
          <div className="mt-6 flex items-start gap-2 rounded-md bg-accent-50 border border-accent-200 px-4 py-3 text-sm text-accent-900">
            <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-accent-700" />
            <span className="flex-1">{success}</span>
            <button
              onClick={clearMessages}
              aria-label="Cerrar"
              className="w-6 h-6 flex items-center justify-center text-accent-700 hover:text-accent-900 cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        )}
        {error && (
          <div className="mt-6 flex items-start gap-2 rounded-md bg-primary-50 border border-primary-200 px-4 py-3 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5 shrink-0" />
            <span className="flex-1">{error}</span>
            <button
              onClick={clearMessages}
              aria-label="Cerrar"
              className="w-6 h-6 flex items-center justify-center text-primary-700 hover:text-primary-900 cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        )}

        {loading && (
          <div className="mt-16 flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
            <span className="text-sm text-foreground-600">Cargando configuración...</span>
          </div>
        )}

        {!loading && !settings && (
          <div className="mt-16 flex flex-col items-center gap-4 text-center">
            <p className="text-foreground-600">No se pudo cargar la configuración.</p>
            <button
              onClick={reload}
              className="rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer"
            >
              Reintentar
            </button>
          </div>
        )}

        {!loading && settings && (
          <>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              <MonedaSection settings={settings} patch={patch} />
              <ImpuestosSection settings={settings} patch={patch} />
              <DescuentosSection settings={settings} patch={patch} />
              <MetodosPagoSection settings={settings} patch={patch} />
              <EfectivoSection settings={settings} patch={patch} />
              <NumeracionSection settings={settings} patch={patch} />
              <EstadoVentaSection settings={settings} patch={patch} />
              <RedondeoSection settings={settings} patch={patch} />
              <InventarioSection settings={settings} patch={patch} />
              <ConfirmacionSection settings={settings} patch={patch} />
              <ImpresionSection settings={settings} patch={patch} />
              <InfoFacturaSection settings={settings} patch={patch} />
              <MensajeFinalSection settings={settings} patch={patch} />
            </div>

            {/* Barra de acciones */}
            <div className="mt-8 sticky bottom-0 -mx-4 md:-mx-6 px-4 md:px-6 py-4 bg-background-50/90 backdrop-blur border-t border-background-200">
              <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-end">
                <button
                  onClick={() => setRestoreOpen(true)}
                  disabled={saving}
                  className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-60 disabled:cursor-not-allowed transition whitespace-nowrap cursor-pointer"
                >
                  Restaurar valores predeterminados
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !dirty}
                  className="rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap flex items-center justify-center gap-2 transition cursor-pointer"
                >
                  {saving && (
                    <div className="w-4 h-4 rounded-full border-2 border-background-50 border-t-transparent animate-spin" />
                  )}
                  {saving ? 'Guardando...' : 'Guardar configuración'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal restaurar */}
      <Modal
        open={restoreOpen}
        onClose={() => setRestoreOpen(false)}
        title="Restaurar configuración"
        footer={
          <>
            <button
              onClick={() => setRestoreOpen(false)}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 transition whitespace-nowrap cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleRestoreConfirm}
              disabled={saving}
              className="rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
            >
              {saving ? 'Restaurando...' : 'Sí, restaurar'}
            </button>
          </>
        }
      >
        ¿Está seguro de que desea restaurar la configuración predeterminada?
      </Modal>

      {/* Modal cambios sin guardar */}
      <Modal
        open={blocker.state === 'blocked'}
        onClose={() => blocker.reset()}
        title="Hay cambios sin guardar"
        footer={
          <>
            <button
              onClick={() => blocker.reset()}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 transition whitespace-nowrap cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleBlockDiscard}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 transition whitespace-nowrap cursor-pointer"
            >
              Descartar cambios
            </button>
            <button
              onClick={handleBlockSave}
              disabled={saving}
              className="rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
            >
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </>
        }
      >
        Has realizado cambios sin guardar. ¿Deseas guardarlos antes de salir?
      </Modal>
    </>
  );
}